import { useState } from "react";
import { Bot, ChevronDown, Infinity as InfinityIcon } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useStaggerIn } from "@/components/Reveal";
import shipConsoleLogo from "../assets/Logos/ShipConsole.webp";

import freelanceLogo from "@/assets/Logos/duck.webp";

export const experiences = [
  {
    id: 1,
    title: "Software Engineer – DevOps & Backend",
    company: "ShipConsole",
    employmentType: "Full-time",
    location: "Hyderabad, Onsite",
    period: "Jan 2024 – Present",
    role: "devops",
    logo: shipConsoleLogo,
    description: [
      "Managing ShipConsole's AWS cloud infrastructure: keeping servers healthy, deployments fast and costs under control.",
      "Built Python and Bash automation for service lifecycles and user-audit reporting, reducing manual interventions by 60%.",
      "Optimized AWS CodePipeline workflows with multi-stage Docker builds, cutting deployment time by 35%.",
      "Set up end-to-end monitoring with Prometheus, Grafana and exporters, plus multi-tier alerting for infrastructure and ECS services, cutting monitoring costs by 45% and speeding up issue resolution.",
      "Reduced AWS spend by 20% through right-sizing, and improved app performance by 25% with K6 load testing.",
      "Awarded Performer of the Year 2024 for operational excellence and automation.",
    ],
  },
  {
    id: 2,
    title: "Freelance Software Engineer – AI & Automation",
    company: "Self-Employed",
    employmentType: "Freelance",
    location: "Remote",
    period: "",
    role: "ai",
    logo: freelanceLogo,
    description: [
      "Building AI-powered automation platforms for businesses with Python, FastAPI and API-driven architecture.",
      "Built multi-platform content generation for LinkedIn, YouTube, Facebook and X, plus lead scraping and qualification pipelines.",
      "Automated SEO blogging workflows integrated with WordPress, using custom AI agents aligned to each company's brand.",
      "Developed RAG-based knowledge APIs with vector embeddings and LLM inference for semantic search and private AI serving.",
    ],
  },
];

// "Jan 2024" -> "2y 7m" relative to today
function calcDuration(startLabel?: string): string {
  if (!startLabel) return "";
  const start = new Date(startLabel);
  if (isNaN(start.getTime())) return "";
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 0) return "";
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  return [years ? `${years}y` : "", remMonths || !years ? `${remMonths}m` : ""].filter(Boolean).join(" ");
}

interface RowProps {
  experience: typeof experiences[0];
  isLast: boolean;
  defaultOpen?: boolean;
  stagger?: boolean;
}

const ExperienceRow = ({ experience, isLast, defaultOpen = false, stagger = false }: RowProps) => {
  const [expanded, setExpanded] = useState(defaultOpen);
  const [imgError, setImgError] = useState(false);

  const FallbackIcon = experience.role === "devops" ? InfinityIcon : Bot;
  const startLabel = experience.period.split("–")[0]?.trim();
  const duration = calcDuration(startLabel);
  const meta = [experience.employmentType, experience.period, duration].filter(Boolean);

  return (
    <div {...(stagger ? { "data-stagger": "" } : {})} className={isLast ? "" : "border-b border-border/40"}>
      {/* Whole header row toggles the details: big tap target on phones */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-start gap-3 py-4 text-left"
      >
        <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
          {!imgError ? (
            <img src={experience.logo} alt="" className="w-full h-full object-contain rounded-lg" onError={() => setImgError(true)} />
          ) : (
            <FallbackIcon className="w-5 h-5 text-foreground/60" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <h3 className="text-sm font-semibold leading-snug text-foreground">
              {experience.title} · {experience.company}
            </h3>
            {experience.location && <span className="text-xs text-foreground/60">{experience.location}</span>}
          </div>
          <p className="mt-0.5 text-xs text-foreground/60">
            {meta.map((m, i) => (
              <span key={m}>
                {i > 0 && <span className="mx-1.5 text-foreground/30">|</span>}
                {m}
              </span>
            ))}
          </p>
        </div>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-foreground/60 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {/* Full width under the logo, one short paragraph per point */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-2.5 pb-5">
              {experience.description.map((item) => (
                <p key={item} className="text-[13px] sm:text-sm leading-relaxed text-foreground/70">
                  {item}
                </p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// `page`: the /experience page (big heading, rows stagger in, latest role open). Otherwise the home section.
export function Experience({ page = false }: { page?: boolean }) {
  const scope = useStaggerIn<HTMLDivElement>();
  const stagger = page ? { "data-stagger": "" } : {};
  return (
    <section
      id="experience"
      className={page ? "relative px-4 pb-20 pt-12 sm:px-6 sm:pt-16 bg-background" : "relative py-6 md:py-8 px-4 sm:px-6 bg-background"}
    >
      <div className="site-frame" ref={scope}>
        {page ? (
          <div className="mb-8 sm:mb-10">
            <h1 {...stagger} className="mb-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">Experience</h1>
            <p {...stagger} className="text-muted-foreground">Where I've worked and what I shipped there. Tap a role for the details.</p>
          </div>
        ) : (
          <div className="mb-4 md:mb-5 text-left">
            <h2 className="text-[15px] md:text-base font-semibold tracking-tight text-foreground">
              Experience
            </h2>
          </div>
        )}

        <div className="border-t border-border/40">
          {experiences.map((exp, i) => (
            <ExperienceRow key={exp.id} experience={exp} isLast={i === experiences.length - 1} defaultOpen={page && i === 0} stagger={page} />
          ))}
        </div>
      </div>
    </section>
  );
}
